import { CardActionArea, Slide, Typography, useTheme } from '@mui/material'
import {
    FlexBox,
    FlexBoxColumn,
    FlexBoxSpaceBetween,
    SymbolButton
} from 'components/atoms'
import { AllOrdersSelector, RunnerBet } from 'components/molecules'
import { TBet, useBetContext } from 'contexts/BetContext'
import { useTranslation } from 'contexts/TranslationContext'
import { TrashIcon } from 'icons'
import { BETS_CONFIG } from 'models/betTypes'
import { Fragment, useMemo, useState } from 'react'

export const BetPlanner = () => {
    const theme = useTheme()
    const translation = useTranslation()
    const {
        race,
        selectedFormulaKey,
        setFormulaKeyBets,
        selectedBets,
        selectedBetType,
        setRunnerSelectionVisible,
        handleTrackingEvent
    } = useBetContext()
    const [slideIn, setSlideIn] = useState(false)

    const currentBets = useMemo(() => {
        const betList = Array.from(selectedBets?.values() || [])
        if (betList.length === 0) return {}
        setSlideIn(true)

        return betList.reduce(
            (
                acc: {
                    [designation: string]: TBet[]
                },
                bet
            ) => ({
                ...acc,
                [bet.designation!]: [...(acc[bet.designation!] || []), bet]
            }),
            {}
        )
    }, [selectedBets.size, selectedBetType, selectedFormulaKey, race?.id])

    const showTrashIcon = useMemo(() => {
        return selectedBetType === 'S'
            ? selectedBets.size > 2
            : selectedBets.size >= 1
    }, [selectedBets.size, selectedBetType])

    return (
        <FlexBoxColumn sx={{ width: '100%', pb: 30 }}>
            <FlexBoxSpaceBetween
                sx={{
                    width: `calc(100% - ${theme.spacing(4)})`,
                    minHeight: '20px',
                    mt: selectedBets.size ? 0 : 4
                }}
            >
                <Typography
                    variant="text-12-semi-bold"
                    sx={
                        selectedBets.size
                            ? {}
                            : { width: '100%', textAlign: 'center', mb: 1 }
                    }
                >
                    {selectedBets.size
                        ? `${translation.mySelection} :`
                        : translation.noHorseSelected}
                </Typography>
                {showTrashIcon && (
                    <CardActionArea
                        sx={{ width: 'fit-content' }}
                        onClick={() => {
                            setSlideIn(false)
                            setTimeout(() => {
                                setFormulaKeyBets(selectedFormulaKey, new Map())
                                void handleTrackingEvent({
                                    eventType: 'BET',
                                    actionType: 'REMOVE_ALL'
                                })
                            }, 300)
                        }}
                    >
                        <TrashIcon />
                    </CardActionArea>
                )}
            </FlexBoxSpaceBetween>

            <Slide
                in={slideIn}
                direction={slideIn ? 'left' : 'right'}
                timeout={300}
            >
                <FlexBoxColumn sx={{ width: '100%' }}>
                    {Object.values(currentBets)?.map((bets) => (
                        <Fragment key={`runner-bet-${bets[0].id}`}>
                            <FlexBox sx={{ mt: 1, width: '100%' }}>
                                <RunnerBet betIds={bets.map((bet) => bet.id)} />
                            </FlexBox>
                            {BETS_CONFIG[selectedBetType]?.order && (
                                <>
                                    <Typography
                                        variant="text-12-regular"
                                        sx={{
                                            color: theme.palette.grey[500],
                                            mt: 1,
                                            px: 2
                                        }}
                                    >
                                        {translation.orderAvailable}
                                    </Typography>
                                    <AllOrdersSelector bet={bets[0]} />
                                </>
                            )}
                        </Fragment>
                    ))}
                </FlexBoxColumn>
            </Slide>

            {(selectedBetType === 'S' || selectedBets.size === 0) && (
                <FlexBox
                    sx={{
                        width: '100%',
                        mt: 1,
                        justifyContent: 'center'
                    }}
                >
                    <SymbolButton
                        symbol="+"
                        label={translation.add}
                        onClick={() => {
                            setRunnerSelectionVisible(true)
                            void handleTrackingEvent({
                                eventType: 'BET',
                                actionType: 'ADD'
                            })
                        }}
                        sx={{ py: 1, px: 4 }}
                    />
                </FlexBox>
            )}
        </FlexBoxColumn>
    )
}
